import { rufe } from './client';

/**
 * Honorartabelle, Rahmenvereinbarung und das Honorar am Einsatz (Kap. 12).
 *
 * ============================================================================
 * BETRÄGE SIND ZEICHENKETTEN
 * ============================================================================
 *
 * Sie kommen als Text aus der API und gehen als Text zurück — RND-01 verbietet
 * Gleitkomma für Geldbeträge, und `JSON.parse` macht aus jeder Zahl eine
 * Gleitkommazahl. Gerechnet wird hier nichts; die Maske zeigt an, was der
 * Server gerechnet hat.
 *
 * ============================================================================
 * WER DIESE AUFRUFE MACHEN DARF
 * ============================================================================
 *
 * `artikel/honorar_sehen` für jede Auskunft, `artikel/honorar_aendern` für jede
 * Änderung (RES-73). Die Maske prüft das ebenfalls — aber nur, um Knöpfe
 * auszublenden. Entschieden wird serverseitig; eine Oberfläche, die eine
 * Berechtigung durchsetzt, ist keine.
 */

export type Leistungseinheit =
  | 'tag'
  | 'halbtag'
  | 'block'
  | 'stunde'
  | 'einsatz'
  | 'kilometer'
  | 'nacht';

/**
 * Worin eine Kategorie zählt — im Wortlaut des Hauses.
 *
 * `einsatz` heißt hier **„je Training"** und nicht „je Einsatz". Auftraggeber am
 * 12.09.2026: „es wird immer von Tag gesprochen, es wird aber je Training
 * vergütet." Der Wert in der Datenbank bleibt `einsatz` — 0183 hat ihn mit genau
 * dieser Bedeutung eingeführt („die Pauschale: einmal je Einsatz, Menge immer
 * 1"), und ein zweiter Wert für dieselbe Sache wäre einer zu viel.
 *
 * Der Unterschied ist die Blickrichtung: Die Datenbank sieht den Einsatz eines
 * Trainers an einem Termin, das Haus sieht das Training. Gerechnet wird mit
 * derselben Zahl.
 */
export const EINHEITSNAME: Readonly<Record<Leistungseinheit, string>> = {
  tag: 'je Tag',
  halbtag: 'je halbem Tag',
  block: 'je Block',
  stunde: 'je Stunde',
  einsatz: 'je Training',
  kilometer: 'je Kilometer',
  nacht: 'je Nacht',
};

/**
 * Die beiden Tarife (RES-68, Migrationen 0186/0187).
 *
 * Die Tabelle hat zwei Achsen in der Maske: **Zeilen sind Kategorien** — je
 * Fahrtraining eine, dazu zwei Stundenzeilen (Stundensatz, Stundensatz
 * Koordinator), die an keinem Training hängen —, **Spalten sind Tarife**.
 *
 * Der Stundensatz war bis zum 12.09.2026 eine dritte Spalte. Das war falsch:
 * „beim honorar ist der stundensatz eine eigene kategorie, wie die
 * fahrzeugklassen" (Auftraggeber). Als Spalte hing er an einem Training —
 * es gab einen Pkw-Stundensatz und einen BKF-Stundensatz, und die Tabelle
 * stellte damit eine Frage, die das Haus nicht stellt.
 */
export type Honorartarif = 'wochentag' | 'wochenende';

/** Die Reihenfolge der Spalten — eine Stelle, alle Masken. */
export const TARIFE: readonly Honorartarif[] = ['wochentag', 'wochenende'];

export const TARIFNAME: Readonly<Record<Honorartarif, string>> = {
  wochentag: 'Wochentag',
  wochenende: 'Wochenende',
};

/**
 * Ein Dezimaltext aus der API als deutscher Betrag — OHNE Gleitkomma.
 *
 * `numeric(14,4)` kommt als „120.0000" an. Der Weg über `Number` wäre kürzer und
 * wäre Gleitkomma; RND-01 verbietet das für Geldbeträge, und hier ist es nicht
 * einmal nötig: Nachkommanullen abschneiden ist eine Textarbeit.
 *
 * Zwei Stellen bleiben immer stehen — „120," sieht nach einem abgebrochenen Feld
 * aus. Mehr als zwei bleiben, wenn sie etwas bedeuten: Ein Kilometergeld von
 * 0,305 Euro ist keine Rundung wert.
 */
export function alsBetragstext(wert: string): string {
  const [ganz, bruch = ''] = wert.split('.');
  return `${ganz ?? '0'},${bruch.replace(/0+$/, '').padEnd(2, '0')}`;
}

export type Berechnung = 'prozent' | 'pauschale' | 'je_einheit';

export const BERECHNUNGSNAME: Readonly<Record<Berechnung, string>> = {
  prozent: 'Prozent vom Grundhonorar',
  pauschale: 'Pauschale je Einsatz',
  je_einheit: 'je Einheit',
};

export type Stufe = 'allgemein' | 'ressource' | 'freihand';

/** RES-69: welche Stufe den Satz geliefert hat — der Wortlaut für die Maske. */
export const STUFENNAME: Readonly<Record<Stufe, string>> = {
  allgemein: 'allgemeine Vorgabe',
  ressource: 'eigener Satz',
  freihand: 'frei vereinbart',
};

export type Leistungsartzeile = {
  readonly schluessel: string;
  readonly bezeichnung: string;
  readonly einheit: Leistungseinheit;
  readonly reihenfolge: number;
  readonly aktiv: boolean;
  readonly bemerkung: string | null;
  /**
   * Das Fahrtraining, für das dieser Eintrag gilt (0188).
   *
   * `null` bei Stundensatz und Koordinator — „nicht in abhängigkeit zum
   * training". Die Verbindung trägt dieses Feld und nicht der Schlüssel; der ist
   * nur ein lesbarer Name, der aus der Artikelnummer entstanden ist.
   */
  readonly artikel: string | null;
  readonly artikelnummer: string | null;
};

/** Ein Fahrtraining, für das noch kein Honorareintrag steht (0188). */
export type TrainingOhneEintrag = {
  readonly artikel: string;
  readonly artikelnummer: string;
  readonly bezeichnung: string;
};

export type Zuschlagsartzeile = {
  readonly schluessel: string;
  readonly bezeichnung: string;
  readonly einheit: string | null;
  readonly reihenfolge: number;
  readonly aktiv: boolean;
  readonly bemerkung: string | null;
};

export type Grundzeile = {
  readonly schluessel: string;
  readonly bezeichnung: string;
  readonly reihenfolge: number;
  readonly aktiv: boolean;
  readonly bemerkung: string | null;
};

export type Honorarsatzzeile = {
  readonly id: string;
  readonly leistungsart: string;
  readonly leistungsartName: string;
  readonly einheit: Leistungseinheit;
  readonly tarif: Honorartarif;
  /** 0191: das Gefäß. Die Stufe steht dort und nicht am Satz. */
  readonly tabelle: string;
  readonly tabellenkennung: string;
  readonly betrag: string;
  readonly gueltigAb: string;
  readonly gueltigBis: string | null;
  readonly bemerkung: string | null;
  /** 0190: der Name des Zuges, in dem dieser Satz entstand. */
  readonly fassung: string | null;
  readonly inGebrauch: boolean;
};

/**
 * Eine FASSUNG der Honorartabelle (RES-68, RES-72, 0190).
 *
 * ============================================================================
 * DAS IST DIE ANTWORT AUF „WIE DIE PREISLISTEN"
 * ============================================================================
 *
 * Auftraggeber am 12.09.2026: „Die honorarsätze sollen konfigurierbar sein und
 * wie die Preislisten für die Produkte mit einem Datum versehen werden, um später
 * anpassbar und nachvollziehbar zu sein."
 *
 * Bei den Preislisten ist die Übersicht eine Liste von **Listen** — Kennung,
 * Bezeichnung, „gültig ab … bis", Zahl der Preiszeilen. Bei den Honoraren stand
 * an derselben Stelle eine Liste von **Zellen**: sechsundsiebzig Beträge mal ihre
 * Fassungen, nach Kategorie sortiert. Beides ist dieselbe Auskunft, und nur das
 * erste ist eine Übersicht.
 *
 * Eine Fassung ist dabei **kein eigenes Objekt** in der Datenbank, sondern ein
 * Tag mit einem Namen: alle Sätze mit demselben `gueltigAb`. Warum kein Gefäß wie
 * `fach.preisliste` — das steht in Migration 0190.
 */
export type Fassungszeile = {
  readonly gueltigAb: string;
  /** Der Name des Zuges, oder null — dann heißt die Fassung nach ihrem Datum. */
  readonly bezeichnung: string | null;
  /** Wie viele Sätze an diesem Tag beginnen. 0 heißt: hier endete etwas. */
  readonly beginnend: number;
  /** Wie viele Sätze an diesem Tag gelten — die gefüllten Zellen der Tabelle. */
  readonly geltend: number;
  readonly inGebrauch: boolean;
};

export type Honorarzuschlagzeile = {
  readonly id: string;
  readonly leistungsart: string;
  readonly leistungsartName: string;
  readonly zuschlagsart: string;
  readonly zuschlagsartName: string;
  /** 0191: dasselbe Gefäß wie beim Satz. */
  readonly tabelle: string;
  readonly tabellenkennung: string;
  readonly berechnung: Berechnung;
  readonly einheit: string | null;
  readonly wert: string;
  readonly gueltigAb: string;
  readonly gueltigBis: string | null;
  readonly bemerkung: string | null;
  readonly inGebrauch: boolean;
};

/**
 * Was am Stichtag gilt — eine Zeile je ZELLE der Tabelle.
 *
 * Achtunddreißig Kategorien mal zwei Tarife sind sechsundsiebzig Zeilen, und
 * jede darf leer sein (`betrag === null`). Genau deshalb kommen auch die leeren
 * mit: Ein Gitter mit Lücken entsteht nur, wenn die Lücken eine Zeile haben.
 */
export type Wirksamzeile = {
  readonly leistungsart: string;
  readonly leistungsartName: string;
  readonly einheit: Leistungseinheit;
  /** Die Spalte, nach der gefragt wurde. */
  readonly tarif: Honorartarif;
  readonly stufe: 'allgemein' | 'ressource' | null;
  /**
   * Der Betrag kommt aus einem anderen Tarif — und da es nur einen Ersatzweg
   * gibt, heißt das immer: aus dem Wochentagstarif, weil für diese Kategorie
   * kein Wochenendtarif hinterlegt ist (0186).
   */
  readonly ersatzweise: boolean;
  readonly betrag: string | null;
  /**
   * Der Zeitraum, in dem dieser Betrag WIRKLICH gilt — der Schnitt aus Gefäß und
   * Zeile (0191).
   *
   * Nicht der der Zeile: Eine unbefristete Zeile in einer Vereinbarung, die zum
   * 30.06. endet, gilt bis zum 30.06. Die Datenbank rechnet das mit
   * `greatest`/`least`, genau wie `fach.preiskandidaten` bei den Preisen.
   */
  readonly gueltigAb: string | null;
  readonly gueltigBis: string | null;
  /** 0191: aus welchem Gefäß der Betrag kam — „STANDARD" oder eine Vereinbarung. */
  readonly tabellenkennung: string | null;
};

/**
 * Eine HONORARTABELLE — das Gefäß (RES-68, RES-69, 0191).
 *
 * ============================================================================
 * DAS IST DIE ANTWORT AUF „WIE DIE PRODUKTPREISLISTE"
 * ============================================================================
 *
 * Auftraggeber am 12.09.2026: „Die honorarpreisliste soll sich konfigurieren
 * lassen, wie die produktpreisliste." Und: „Es soll immer nur eine gültige
 * Preisliste geben, plus die Möglichkeit der individuellen Vereinbarungen mit den
 * Trainern selbst."
 *
 * Dieselben Felder wie eine Preisliste — Kennung, Bezeichnung, Gültigkeit, die
 * Zahl der Zeilen darin. **Ohne Rangstufe und ohne „vorrangig":** Die gibt es bei
 * den Preisen, weil dort mehrere Listen gleichzeitig gelten und eine gewinnen
 * muss. Hier gilt je Stichtag genau eine — und damit gibt es nichts zu ordnen.
 *
 * `ressource === null` IST die Stufe: die allgemeine Vorgabe des Hauses. Gesetzt
 * ist es die individuelle Vereinbarung mit genau diesem Trainer.
 */
export type Honorartabellenzeile = {
  readonly id: string;
  readonly kennung: string;
  readonly bezeichnung: string;
  readonly ressource: string | null;
  readonly ressourceName: string | null;
  readonly gueltigAb: string;
  readonly gueltigBis: string | null;
  readonly bemerkung: string | null;
  readonly geloeschtAm: string | null;
  /** Gilt sie am Geschäftstag? Kommt aus der Datenbank, nicht aus `new Date()`. */
  readonly gilt: boolean;
  readonly saetze: number;
  readonly zuschlaege: number;
};

export type Honorartabelle = {
  readonly leistungsarten: readonly Leistungsartzeile[];
  readonly zuschlagsarten: readonly Zuschlagsartzeile[];
  readonly uebersteuerungsgruende: readonly Grundzeile[];
  /** Die Honorartabellen des Hauses (0191) — allgemeine und eigene. */
  readonly tabellen: readonly Honorartabellenzeile[];
  /** Welche davon die Sätze unten zeigen. */
  readonly gewaehlt: string | null;
  readonly saetze: readonly Honorarsatzzeile[];
  /** Die Fassungen der gewählten Tabelle, die neueste zuerst (0190). */
  readonly fassungen: readonly Fassungszeile[];
  readonly zuschlaege: readonly Honorarzuschlagzeile[];
  /**
   * Fahrtrainings ohne Honorareintrag (0188) — höchstens 25 Namen.
   *
   * Seit die Kategorie das Training IST, entsteht mit jedem neuen Training eine
   * Lücke. In einer Tabelle mit sechsunddreißig Zeilen fällt eine fehlende nicht
   * auf; deshalb fragt die Maske bei jedem Aufruf danach.
   */
  readonly ohneEintrag: readonly TrainingOhneEintrag[];
  readonly ohneEintragGesamt: number;
};

/**
 * Was in der allgemeinen Tabelle gilt — aus den Sätzen gebildet.
 *
 * `GET /honorare/tabelle` liefert alle FASSUNGEN der allgemeinen Sätze, auch die
 * abgelaufenen; das Gitter braucht je Zelle die EINE, die heute gilt. Die
 * Trainerakte bekommt dasselbe fertig vom Server (`wirksam`) — dort kreuzen sich
 * zwei Stufen, und das entscheidet die Datenbank. Hier ist es eine Stufe und
 * eine Auswahl über das Datum, und die läuft in der Maske ab, statt einen
 * zweiten Endpunkt zu brauchen.
 */
export function wirksamAusSaetzen(
  tabelle: Honorartabelle,
  stichtag: string,
): readonly Wirksamzeile[] {
  const zeilen: Wirksamzeile[] = [];
  /*
   * 0191: Welche Stufe das gewählte Gefäß ist. Sie steht an der Tabelle und
   * nicht am Satz — und damit gilt sie für jede Zelle dieses Gitters.
   */
  const gefaess = tabelle.tabellen.find((t) => t.id === tabelle.gewaehlt);
  const stufe: 'allgemein' | 'ressource' =
    gefaess?.ressource == null ? 'allgemein' : 'ressource';

  for (const art of tabelle.leistungsarten.filter((a) => a.aktiv)) {
    for (const tarif of TARIFE) {
      const satz = tabelle.saetze.find(
        (s) =>
          s.leistungsart === art.schluessel
          && s.tarif === tarif
          && s.gueltigAb <= stichtag
          && (s.gueltigBis === null || s.gueltigBis >= stichtag),
      );
      zeilen.push({
        leistungsart: art.schluessel,
        leistungsartName: art.bezeichnung,
        /* Die Einheit gehört zur Kategorie und nicht zum Tarif (0187). */
        einheit: art.einheit,
        tarif,
        /*
         * 0191: Die Stufe kommt aus dem GEFÄSS, das die Maske gerade zeigt.
         * Innerhalb einer Tabelle ist sie für jede Zelle dieselbe — die Frage
         * „allgemein oder eigen" wird beim Wählen der Tabelle entschieden und
         * nicht je Zelle.
         */
        stufe: satz === undefined ? null : stufe,
        tabellenkennung: satz?.tabellenkennung ?? null,
        /*
         * In der allgemeinen Tabelle wird NICHT ersetzt: Sie ist die Stelle, an
         * der man sieht, was fehlt. Der Ersatz ist eine Eigenschaft der
         * Ermittlung am Einsatz, nicht der Tabelle.
         */
        ersatzweise: false,
        betrag: satz?.betrag ?? null,
        gueltigAb: satz?.gueltigAb ?? null,
        gueltigBis: satz?.gueltigBis ?? null,
      });
    }
  }

  return zeilen;
}

export type Trainerhonorar = {
  readonly ressource: string;
  readonly kennung: string;
  readonly bezeichnung: string;
  readonly zugehoerigkeit: 'intern' | 'extern' | null;
  /** SEINE Honorartabellen (0191) — die geltende, die abgelaufenen, die gelöschten. */
  readonly tabellen: readonly Honorartabellenzeile[];
  readonly saetze: readonly Honorarsatzzeile[];
  readonly zuschlaege: readonly Honorarzuschlagzeile[];
  readonly wirksam: readonly Wirksamzeile[];
};

/**
 * Der ganze Stand der Honorarpflege — zu EINER Tabelle (0191).
 *
 * Ohne `gewaehlt` antwortet der Server mit der allgemeinen Tabelle, die am
 * Geschäftstag gilt. Das ist die Frage, die die Maske beim Öffnen stellt.
 */
export async function tabelle(gewaehlt?: string): Promise<Honorartabelle> {
  return rufe(
    gewaehlt === undefined
      ? '/honorare/tabelle'
      : `/honorare/tabelle?tabelle=${encodeURIComponent(gewaehlt)}`,
  );
}

// ---------------------------------------------------------------------------
// Die Honorartabellen als Gefäß (RES-68, RES-69, 0191)
// ---------------------------------------------------------------------------

export async function tabellen(eingabe?: {
  readonly auchGeloeschte?: boolean;
  readonly ressource?: string;
}): Promise<readonly Honorartabellenzeile[]> {
  const teile = new URLSearchParams();
  if (eingabe?.auchGeloeschte === true) teile.set('auchGeloeschte', '1');
  if (eingabe?.ressource !== undefined) teile.set('ressource', eingabe.ressource);
  const pfad = teile.size === 0 ? '/honorare/tabellen' : `/honorare/tabellen?${teile.toString()}`;
  const antwort = await rufe<{ readonly tabellen: readonly Honorartabellenzeile[] }>(pfad);
  return antwort.tabellen;
}

export async function tabelleAnlegen(eingabe: {
  readonly kennung: string;
  readonly bezeichnung: string;
  /** null ist die allgemeine Vorgabe, gesetzt die Vereinbarung mit einem Trainer. */
  readonly ressource: string | null;
  readonly gueltigAb: string;
  readonly gueltigBis: string | null;
  readonly bemerkung: string | null;
}): Promise<{ readonly id: string }> {
  return rufe('/honorare/tabellen', { verfahren: 'POST', rumpf: eingabe });
}

/**
 * Eine Honorartabelle ändern (0191).
 *
 * OHNE `kennung` und ohne `ressource`: Die Kennung steht in jeder
 * Herkunftsauskunft, und die Ressource zu wechseln machte aus einer Vereinbarung
 * mit A eine mit B — bei gleichbleibenden Beträgen.
 */
export async function tabelleAendern(
  id: string,
  eingabe: {
    readonly bezeichnung?: string;
    readonly gueltigAb?: string;
    readonly gueltigBis?: string | null;
    readonly bemerkung?: string | null;
  },
): Promise<{ readonly geaendert: boolean }> {
  return rufe(`/honorare/tabellen/${encodeURIComponent(id)}`, {
    verfahren: 'PATCH',
    rumpf: eingabe,
  });
}

export async function tabelleLoeschen(id: string): Promise<{ readonly geloescht: boolean }> {
  return rufe(`/honorare/tabellen/${encodeURIComponent(id)}`, { verfahren: 'DELETE' });
}

/**
 * Eine Honorartabelle kopieren (0192).
 *
 * Auftraggeber am 12.09.2026: „honorartabellen sollen kopierbar sein."
 *
 * Der häufigste Fall: die allgemeine Tabelle in eine Vereinbarung mit einem
 * Trainer kopieren, dann zwei Zellen ändern. Von Hand wären das
 * sechsundsiebzig getippte Beträge, von denen vierundsiebzig schon woanders
 * stehen.
 *
 * KOPIERT WIRD EIN STAND, KEINE GESCHICHTE — was in der Vorlage am `stichtag`
 * gilt (ohne Angabe: am Beginn der Kopie). Und die Kopie ist **keine
 * Verbindung**: Danach stehen zwei Tabellen nebeneinander. Wer will, dass die
 * Standardtabelle weiter durchschlägt, lässt die Zelle **leer** (0192).
 */
export async function tabelleKopieren(eingabe: {
  readonly vorlage: string;
  readonly kennung: string;
  readonly bezeichnung: string;
  readonly ressource: string | null;
  readonly gueltigAb: string;
  readonly gueltigBis: string | null;
  readonly bemerkung: string | null;
  readonly stichtag: string | null;
}): Promise<{ readonly id: string; readonly saetze: number; readonly zuschlaege: number }> {
  return rufe('/honorare/tabellen/kopie', { verfahren: 'POST', rumpf: eingabe });
}

/**
 * Die Nachfolgerin — beenden und beginnen in einem Griff (0191).
 *
 * Aus „immer nur eine gültige" folgt, dass der Übergang die eigentliche Handlung
 * ist: Die Tabelle für 2027 kann erst entstehen, wenn die geltende zum 31.12.2026
 * endet. In zwei Schritten aus der Maske geht das schief, sobald jemand die
 * Reihenfolge vertauscht.
 *
 * `uebernehmen` nimmt die Beträge mit, wie sie am Übergangstag gelten. Ohne das
 * ist die neue Tabelle leer — eine neue vertragliche Zusage und keine Kopie.
 */
export async function nachfolgerAnlegen(eingabe: {
  readonly vorgaenger: string;
  readonly kennung: string;
  readonly bezeichnung: string;
  readonly gueltigAb: string;
  readonly bemerkung: string | null;
  readonly uebernehmen: boolean;
}): Promise<{ readonly id: string; readonly uebernommen: number }> {
  return rufe('/honorare/tabellen/nachfolger', { verfahren: 'POST', rumpf: eingabe });
}

/**
 * Die Honorarseite eines Trainers — zu einem STICHTAG (RES-69, 0190).
 *
 * Ohne Stichtag antwortet der Server mit dem Geschäftstag. Mit einem beantwortet
 * er die Frage, die man im Herbst stellt: „Was bekommt er ab Januar?" Die
 * zweistufige Ermittlung rechnet die Datenbank, und sie braucht dafür ein Datum —
 * hier wird nur gesagt, welches.
 */
export async function trainerhonorar(
  ressource: string,
  stichtag?: string,
): Promise<Trainerhonorar> {
  const pfad = `/honorare/ressourcen/${encodeURIComponent(ressource)}`;
  return rufe(
    stichtag === undefined ? pfad : `${pfad}?stichtag=${encodeURIComponent(stichtag)}`,
  );
}

export async function satzAnlegen(eingabe: {
  readonly leistungsart: string;
  readonly tarif: Honorartarif;
  /** 0191: das Gefäß, in dem der Satz steht. */
  readonly tabelle: string;
  readonly betrag: string;
  readonly gueltigAb: string;
  readonly gueltigBis: string | null;
  readonly bemerkung: string | null;
  readonly fassung?: string | null;
}): Promise<{ readonly id: string }> {
  return rufe('/honorare/saetze', { verfahren: 'POST', rumpf: eingabe });
}

/**
 * Die Tabelle fortschreiben — das ganze Gitter in einem Zug (RES-68, RES-72).
 *
 * ============================================================================
 * DAS SIEHT WIE ÄNDERN AUS UND IST FORTSCHREIBEN
 * ============================================================================
 *
 * Wer zwölf Zellen tippt, will einmal speichern. Der Server macht daraus je
 * geänderte Zelle zwei Schritte: den geltenden Satz zum Vortag schließen und den
 * neuen ab `gueltigAb` anlegen. Kein Betrag wird überschrieben, und ein
 * erteilter Auftrag behält seinen Satz.
 *
 * ALLE ZELLEN MITSCHICKEN, auch die unveränderten — der Server vergleicht und
 * lässt liegen, was gleich ist. Eine Maske, die selbst entscheidet, was sich
 * geändert hat, entscheidet es beim nächsten Umbau falsch.
 *
 * `betrag: null` heißt: Dieser Tarif gilt ab dem Datum nicht mehr.
 */
export async function tabelleFortschreiben(eingabe: {
  /** 0191: in welches Gefäß das Gitter geschrieben wird. */
  readonly tabelle: string;
  readonly gueltigAb: string;
  readonly zellen: readonly {
    readonly leistungsart: string;
    readonly tarif: Honorartarif;
    readonly betrag: string | null;
  }[];
  readonly bemerkung: string | null;
  /** 0190: der Name dieser Fassung, oder null — „Tarifrunde 2027". */
  readonly fassung: string | null;
}): Promise<{ readonly geschlossen: number; readonly angelegt: number }> {
  return rufe('/honorare/saetze', { verfahren: 'PUT', rumpf: eingabe });
}

/**
 * Einen Satz schließen (RES-72).
 *
 * DEN BETRAG GIBT ES HIER NICHT, und das ist der Kern der Anforderung: Ein
 * erteilter Auftrag behält seinen Satz. Wer etwas anderes zahlen will, schließt
 * den alten Satz und legt den neuen daneben.
 */
export async function satzSchliessen(
  id: string,
  eingabe: { readonly gueltigBis: string | null; readonly bemerkung?: string | null },
): Promise<{ readonly geaendert: boolean }> {
  return rufe(`/honorare/saetze/${encodeURIComponent(id)}`, {
    verfahren: 'PATCH',
    rumpf: { gueltigBis: eingabe.gueltigBis, bemerkung: eingabe.bemerkung ?? null },
  });
}

export async function satzLoeschen(id: string): Promise<{ readonly geloescht: boolean }> {
  return rufe(`/honorare/saetze/${encodeURIComponent(id)}`, { verfahren: 'DELETE' });
}

/**
 * Eine ganze Fassung verwerfen (RES-72, 0190).
 *
 * Das Gegenstück zum Löschen einer Preisliste: Eine Fassung, die zum falschen
 * Datum steht oder nicht mehr kommen soll, verschwindet in einem Griff — und die
 * Vorgänger, die sie verdrängt hatte, gelten wieder bis zu ihrem alten Ende.
 *
 * EINE FASSUNG MIT EINEM AUFTRAG DARAN BLEIBT STEHEN. Der Server weist das mit
 * `honorarsatz_in_gebrauch` ab; die Maske sperrt den Knopf zusätzlich, damit es
 * nicht erst beim Drücken auffällt.
 */
export async function fassungVerwerfen(
  gueltigAb: string,
  tabelle: string,
): Promise<{ readonly entfernt: number; readonly wiedereroeffnet: number }> {
  return rufe(
    `/honorare/fassungen/${encodeURIComponent(gueltigAb)}?tabelle=${encodeURIComponent(tabelle)}`,
    { verfahren: 'DELETE' },
  );
}

export async function zuschlagAnlegen(eingabe: {
  readonly leistungsart: string;
  readonly zuschlagsart: string;
  /** 0191: dasselbe Gefäß wie beim Satz. */
  readonly tabelle: string;
  readonly berechnung: Berechnung;
  readonly wert: string;
  readonly gueltigAb: string;
  readonly gueltigBis: string | null;
  readonly bemerkung: string | null;
}): Promise<{ readonly id: string }> {
  return rufe('/honorare/zuschlaege', { verfahren: 'POST', rumpf: eingabe });
}

export async function zuschlagSchliessen(
  id: string,
  eingabe: { readonly gueltigBis: string | null },
): Promise<{ readonly geaendert: boolean }> {
  return rufe(`/honorare/zuschlaege/${encodeURIComponent(id)}`, {
    verfahren: 'PATCH',
    rumpf: { gueltigBis: eingabe.gueltigBis, bemerkung: null },
  });
}

export async function zuschlagLoeschen(id: string): Promise<{ readonly geloescht: boolean }> {
  return rufe(`/honorare/zuschlaege/${encodeURIComponent(id)}`, { verfahren: 'DELETE' });
}

/** Die pflegbaren Auswahllisten (SYS-10, RES-70). */
export type Listenname = 'leistungsarten' | 'zuschlagsarten' | 'uebersteuerungsgruende';

export async function auswahlAnlegen(
  liste: Listenname,
  eingabe: {
    readonly schluessel: string;
    readonly bezeichnung: string;
    readonly einheit?: Leistungseinheit | null;
    readonly reihenfolge: number;
    readonly bemerkung: string | null;
    /** 0188: nur bei einer Kategorie — das Fahrtraining, für das sie gilt. */
    readonly artikel?: string | null;
  },
): Promise<{ readonly schluessel: string }> {
  return rufe(`/honorare/listen/${liste}`, { verfahren: 'POST', rumpf: eingabe });
}

export async function auswahlAendern(
  liste: Listenname,
  schluessel: string,
  eingabe: {
    readonly bezeichnung?: string;
    readonly reihenfolge?: number;
    readonly aktiv?: boolean;
    readonly bemerkung?: string | null;
  },
): Promise<{ readonly geaendert: boolean }> {
  return rufe(`/honorare/listen/${liste}/${encodeURIComponent(schluessel)}`, {
    verfahren: 'PATCH',
    rumpf: eingabe,
  });
}

// ---------------------------------------------------------------------------
// Am Einsatz (RES-43, RES-70)
// ---------------------------------------------------------------------------

export type Beauftragungsbefund =
  | 'ja'
  | 'keine_vereinbarung'
  | 'keine_auftragsverarbeitung'
  | 'beides_fehlt'
  | 'nicht_extern';

/** RES-64: der Befund im Wortlaut, den die Disposition liest. */
export const BEFUNDNAME: Readonly<Record<Beauftragungsbefund, string>> = {
  ja: 'Vertragliche Grundlage vorhanden',
  keine_vereinbarung: 'Keine Rahmenvereinbarung',
  keine_auftragsverarbeitung: 'Keine Auftragsverarbeitung',
  beides_fehlt: 'Keine Rahmenvereinbarung, keine Auftragsverarbeitung',
  nicht_extern: 'Wird zugeordnet, nicht beauftragt',
};

export type Honorarpositionszeile = {
  readonly id: string;
  readonly art: 'grundhonorar' | 'zuschlag';
  readonly leistungsart: string | null;
  /** Der Tarif, aus dem der Satz kam. null bei Zuschlag oder freier Vereinbarung. */
  readonly tarif: Honorartarif | null;
  readonly zuschlagsart: string | null;
  readonly bezeichnung: string;
  readonly berechnung: Berechnung;
  readonly menge: string;
  readonly satz: string;
  readonly betrag: string;
  readonly stufe: Stufe;
  readonly tabellensatz: string | null;
  readonly uebersteuerungsgrund: string | null;
  readonly uebersteuerungsgrundName: string | null;
  readonly uebersteuertAm: string | null;
};

export type Einsatzhonorar = {
  readonly belegung: string;
  readonly leistungsart: string | null;
  readonly vereinbarung: string | null;
  readonly vereinbarungKennung: string | null;
  readonly positionen: readonly Honorarpositionszeile[];
  readonly summe: string;
  readonly letzteKostenfreieAbsage: string | null;
  readonly ausfallJetzt: string;
};

export type Vorschauzeile = {
  readonly art: 'grundhonorar' | 'zuschlag';
  readonly schluessel: string;
  readonly bezeichnung: string;
  readonly berechnung: Berechnung;
  readonly menge: string;
  readonly satz: string;
  readonly betrag: string;
  readonly stufe: Stufe;
  /** `offen` will bestätigt werden — das System kann es nicht entscheiden. */
  readonly zutreffend: 'ja' | 'offen';
  readonly begruendung: string;
};

export type Honorarvorschau = {
  readonly leistungsart: string;
  readonly leistungsartName: string;
  readonly einheit: Leistungseinheit;
  /** Der Tarif, nach dem gerechnet wurde — aus dem Termin oder angegeben. */
  readonly tarif: Honorartarif;
  /** Gefragt war der Wochenendtarif, gegolten hat der Wochentagstarif (0186). */
  readonly ersatzweise: boolean;
  readonly stufe: Stufe;
  readonly tabellensatz: string | null;
  readonly positionen: readonly Vorschauzeile[];
  readonly summe: string;
  readonly ohneTabellensatz: boolean;
  readonly befund: Beauftragungsbefund;
  readonly vereinbarung: string | null;
};

/**
 * Was der Einsatz kosten würde — vor der Anfrage (RES-70).
 *
 * Sie schreibt nichts. Die Anfrage selbst nimmt dieselben Angaben mit.
 */
export async function vorschau(eingabe: {
  readonly termin: string;
  readonly bedarf: string;
  readonly ressource: string;
  readonly leistungsart: string;
  readonly menge?: string;
  readonly satz?: string;
}): Promise<Honorarvorschau> {
  const teile = new URLSearchParams({
    termin: eingabe.termin,
    bedarf: eingabe.bedarf,
    ressource: eingabe.ressource,
    leistungsart: eingabe.leistungsart,
  });
  if (eingabe.menge !== undefined) teile.set('menge', eingabe.menge);
  if (eingabe.satz !== undefined) teile.set('satz', eingabe.satz);
  return rufe(`/honorare/vorschau?${teile.toString()}`);
}

export async function einsatzhonorar(belegung: string): Promise<Einsatzhonorar> {
  return rufe(`/honorare/einsaetze/${encodeURIComponent(belegung)}`);
}

export async function honorarFestlegen(
  belegung: string,
  eingabe: {
    readonly leistungsart: string;
    readonly menge?: string;
    readonly satz?: string;
    readonly uebersteuerungsgrund?: string;
    readonly zuschlaege?: readonly { readonly zuschlagsart: string; readonly menge: string }[];
  },
): Promise<Einsatzhonorar> {
  return rufe(`/honorare/einsaetze/${encodeURIComponent(belegung)}`, {
    verfahren: 'POST',
    rumpf: eingabe,
  });
}

// ---------------------------------------------------------------------------
// Das Vertragliche (RES-65, RES-86, RES-87)
// ---------------------------------------------------------------------------

export type Vereinbarungsstatus = 'entwurf' | 'aktiv' | 'gekuendigt' | 'beendet';

export const VEREINBARUNGSSTATUSNAME: Readonly<Record<Vereinbarungsstatus, string>> = {
  entwurf: 'Entwurf',
  aktiv: 'aktiv',
  gekuendigt: 'gekündigt',
  beendet: 'beendet',
};

export type Ausfallstufe = {
  readonly id: string;
  readonly abTagenVorEinsatz: number;
  readonly berechnung: 'prozent' | 'pauschale';
  /** `null` ohne `honorar_sehen` — die Frist bleibt sichtbar (SYS-03). */
  readonly wert: string | null;
  readonly bemerkung: string | null;
};

export type Vereinbarungszeile = {
  readonly id: string;
  readonly ressource: string;
  readonly kennung: string;
  readonly gueltigAb: string;
  readonly gueltigBis: string | null;
  readonly kuendigungsfristTage: number | null;
  readonly bedingungen: string | null;
  readonly status: Vereinbarungsstatus;
  readonly unterzeichnetAm: string | null;
  readonly gekuendigtAm: string | null;
  readonly bemerkung: string | null;
  readonly gilt: boolean;
  readonly staffel: readonly Ausfallstufe[];
};

export async function vereinbarungen(ressource: string): Promise<readonly Vereinbarungszeile[]> {
  const antwort = await rufe<{ readonly vereinbarungen: readonly Vereinbarungszeile[] }>(
    `/ressourcen/${encodeURIComponent(ressource)}/vereinbarungen`,
  );
  return antwort.vereinbarungen;
}

export async function vereinbarungAnlegen(
  ressource: string,
  eingabe: {
    readonly kennung: string;
    readonly gueltigAb: string;
    readonly gueltigBis: string | null;
    readonly kuendigungsfristTage: number | null;
    readonly bedingungen: string | null;
    readonly status: Vereinbarungsstatus;
    readonly unterzeichnetAm: string | null;
    readonly bemerkung: string | null;
  },
): Promise<{ readonly id: string }> {
  return rufe(`/ressourcen/${encodeURIComponent(ressource)}/vereinbarungen`, {
    verfahren: 'POST',
    rumpf: eingabe,
  });
}

export async function vereinbarungAendern(
  ressource: string,
  vereinbarung: string,
  eingabe: {
    readonly gueltigBis?: string | null;
    readonly kuendigungsfristTage?: number | null;
    readonly bedingungen?: string | null;
    readonly status?: Vereinbarungsstatus;
    readonly unterzeichnetAm?: string | null;
    readonly gekuendigtAm?: string | null;
    readonly bemerkung?: string | null;
  },
): Promise<{ readonly geaendert: boolean }> {
  return rufe(
    `/ressourcen/${encodeURIComponent(ressource)}/vereinbarungen/${encodeURIComponent(vereinbarung)}`,
    { verfahren: 'PATCH', rumpf: eingabe },
  );
}

/** Eine Stufe der Ausfallstaffel setzen — der Schwellwert ist ihr Schlüssel. */
export async function staffelSetzen(
  ressource: string,
  vereinbarung: string,
  eingabe: {
    readonly abTagenVorEinsatz: number;
    readonly berechnung: 'prozent' | 'pauschale';
    readonly wert: string;
    readonly bemerkung: string | null;
  },
): Promise<{ readonly id: string }> {
  return rufe(
    `/ressourcen/${encodeURIComponent(ressource)}/vereinbarungen/${encodeURIComponent(vereinbarung)}/staffel`,
    { verfahren: 'POST', rumpf: eingabe },
  );
}

export async function staffelEntfernen(
  ressource: string,
  vereinbarung: string,
  stufe: string,
): Promise<{ readonly entfernt: boolean }> {
  return rufe(
    `/ressourcen/${encodeURIComponent(ressource)}/vereinbarungen/${encodeURIComponent(vereinbarung)}/staffel/${encodeURIComponent(stufe)}`,
    { verfahren: 'DELETE' },
  );
}

export type Absagefristzeile = {
  readonly belegung: string;
  readonly ressource: string;
  readonly kennung: string;
  readonly bezeichnung: string;
  readonly beginn: string;
  readonly letzteKostenfreieAbsage: string;
  /** `null` ohne `honorar_sehen` (SYS-03, RES-73). */
  readonly kostenrisiko: string | null;
  readonly honorar: string | null;
  readonly vereinbarung: string | null;
};

/** RES-87: bis wann der Termin kostenfrei absagbar ist, und was danach kostet. */
export async function absagefristen(termin: string): Promise<readonly Absagefristzeile[]> {
  const antwort = await rufe<{ readonly zeilen: readonly Absagefristzeile[] }>(
    `/ressourcen/termine/${encodeURIComponent(termin)}/absagefristen`,
  );
  return antwort.zeilen;
}

// ---------------------------------------------------------------------------
// Die Abrechnungsgrundlage (RES-76, RES-77, RES-62)
// ---------------------------------------------------------------------------

/** Wie die Rückmeldung auf den Einsatz gewirkt hat — siehe Kopf von 0249. */
export const REGELNAME: Readonly<Record<string, string>> = {
  ohne_rueckmeldung: 'ohne Rückmeldung — wie beauftragt',
  wie_beauftragt: 'wie beauftragt',
  ausgefallen: 'ausgefallen — nichts abzurechnen',
  vertreten: 'vertreten — abgerechnet wird die Vertretung',
  zeitanteilig: 'nach tatsächlicher Dauer',
  von_hand_pruefen: 'Dauer abweichend — von Hand prüfen',
  ausfallverguetung: 'abgesagt — Ausfallvergütung',
  abgesagt_kostenfrei: 'abgesagt — kostenfrei',
};

export type Abrechnungszeile = {
  readonly belegung: string;
  readonly termin: string | null;
  readonly beginn: string;
  readonly ende: string;
  readonly produkt: string | null;
  readonly leistung: string | null;
  readonly belegungsstatus: string;
  readonly rueckmeldung: string | null;
  readonly beauftragt: string;
  readonly abzurechnen: string;
  readonly regel: string;
  readonly eingangsrechnung: string | null;
  readonly geprueftAm: string | null;
};

export type Abrechnungsgrundlage = {
  readonly einsaetze: readonly Abrechnungszeile[];
  readonly summen: {
    readonly beauftragt: string;
    readonly abzurechnen: string;
    readonly offen: string;
    readonly abgerechnet: string;
  };
};

export async function abrechnungsgrundlage(
  ressource: string,
  von: string,
  bis: string,
): Promise<Abrechnungsgrundlage> {
  const q = new URLSearchParams({ ressource, von, bis });
  return rufe(`/honorare/abrechnung?${q.toString()}`);
}

export type Pruefergebnis = {
  readonly grundlage: string;
  readonly rechnungsbetrag: string;
  readonly differenz: string;
  readonly vermerkt: boolean;
};

export async function eingangsrechnungPruefen(eingabe: {
  readonly ressource: string;
  readonly von: string;
  readonly bis: string;
  readonly belegungen: readonly string[];
  readonly eingangsrechnung: string;
  readonly rechnungsbetrag: string;
  readonly bemerkung: string | null;
  readonly bestaetigen: boolean;
}): Promise<Pruefergebnis> {
  return rufe('/honorare/abrechnung/pruefen', { verfahren: 'POST', rumpf: eingabe });
}
